/**
 * Project templates API client (Phase 19 + Phase 20).
 *
 * Phase 19: Wraps Phase 18's `GET /api/admin/project-templates` (API-01).
 * Consumed by `useProjectTemplates()` (`frontend/src/hooks/useProjectTemplates.ts`)
 * which hydrates the Template dropdown on the project creation form
 * (`frontend/src/pages/projects/ProjectFormPage.tsx` — see plan 19-06, D-01..D-06).
 *
 * Phase 20 (plan 20-02): Extends with admin CRUD — getProjectTemplate, createProjectTemplate,
 * updateProjectTemplate, softDeleteProjectTemplate. Used by the Admin Templates page
 * (Wave 3, plan 20-07) for template create / edit / soft-delete (UI-04, TPL-04..06).
 *
 * Type mirrors backend `templatePublicShape` from
 * `backend/src/api/schemas/projectTemplate.ts` (Phase 18, frozen).
 */
import { apiGet, apiPost, apiPatch, apiDelete } from './client';

// ===== Types =====

export interface ProjectTemplateState {
  id: number;
  name: string;
  ordering: number;
  color: string; // '#RRGGBB'
}

export interface ProjectTemplate {
  id: number;
  name: string;
  description: string | null;
  isBuiltIn: boolean;
  isLegacy: boolean;
  createdBy: number | null;
  createdAt: string;
  updatedAt: string;
  states: ProjectTemplateState[];
}

/**
 * Template detail returned by GET /admin/project-templates/:id (Phase 18 API-01).
 *
 * IMPORTANT: the backend response schema (templateResponseSchema in
 * `backend/src/api/schemas/projectTemplate.ts`) does NOT include an
 * `inUseCount` field — `fast-json-stringify` strips anything not listed.
 * The "in use by N projects" count for the TPL-06 disabled-soft-delete
 * tooltip (D-05) is computed client-side in ProjectTemplateEditPage from
 * useProjects() — see plan 20-07 Task 2.
 *
 * For now this is a structural alias for ProjectTemplate; we keep the
 * named export so call sites can reference the detail-shape semantics
 * even if the wire shape ever diverges later.
 */
export type ProjectTemplateDetail = ProjectTemplate;

/**
 * Body shape for POST /admin/project-templates (D-03 — admin authors a new template).
 *
 * `description` is OPTIONAL — when the user leaves the textarea blank,
 * the caller MUST omit the key entirely. The backend body schema
 * (`backend/src/api/schemas/projectTemplate.ts` line 73) is
 * `description: { type: 'string', maxLength: 500 }` — sending `null`
 * triggers a 400. See plan 20-07 Task 2 `handleSave` for the canonical
 * spread pattern.
 *
 * `states` are the ordered state list — at least one state required server-side.
 */
export interface CreateProjectTemplateInput {
  name: string;
  description?: string;
  states: Array<{ name: string; ordering: number; color: string }>;
}

/**
 * Body shape for PATCH /admin/project-templates/:id (D-03 — admin renames or
 * reorders/recolors states; TPL-05 forked-snapshot — edits do NOT propagate
 * to projects already created from the template).
 *
 * Any subset is acceptable; `states` when present is the FULL replacement list.
 * Same nullability rule as create: omit `description` when blank — never `null`.
 */
export type UpdateProjectTemplateInput = Partial<CreateProjectTemplateInput>;

// ===== API Functions =====

/**
 * Fetch all active project templates.
 *
 * Server already filters out `is_legacy = true` and soft-deleted rows
 * (Phase 17 D-04..D-06). Client also defensively filters `isLegacy` to
 * ensure no Legacy templates ever reach the picker — cheap, prevents
 * accidental exposure if the server-side filter is ever loosened.
 * (Per 19-CONTEXT.md `<decisions>` Claude's Discretion.)
 */
export async function getProjectTemplates(): Promise<ProjectTemplate[]> {
  const res = await apiGet<{ data: ProjectTemplate[] }>('/admin/project-templates');
  return res.data.filter((t) => !t.isLegacy);
}

export async function getProjectTemplate(id: number): Promise<ProjectTemplateDetail> {
  const res = await apiGet<{ data: ProjectTemplateDetail }>(`/admin/project-templates/${id}`);
  return res.data;
}

export async function createProjectTemplate(
  input: CreateProjectTemplateInput
): Promise<ProjectTemplate> {
  const res = await apiPost<{ data: ProjectTemplate }>('/admin/project-templates', input);
  return res.data;
}

export async function updateProjectTemplate(
  id: number,
  patch: UpdateProjectTemplateInput
): Promise<ProjectTemplate> {
  const res = await apiPatch<{ data: ProjectTemplate }>(`/admin/project-templates/${id}`, patch);
  return res.data;
}

/**
 * Soft-delete a non-built-in template. The server enforces TPL-06
 * (returns 409 if the template is any project's appliedTemplateId);
 * frontend surfaces 409 via AppToast in ProjectTemplateEditPage.
 */
export async function softDeleteProjectTemplate(id: number): Promise<void> {
  await apiDelete<void>(`/admin/project-templates/${id}`);
}
